// Keyboard navigation for the WAI-ARIA combobox pattern. Pairs with
// an input that has role="combobox" and a listbox whose options can
// be identified by stable ids of the shape `${listboxId}-option-${idx}`.
//
// The activedescendant model: the input keeps DOM focus, while the
// "logically focused" option moves via state. The active option's id
// goes into the input's `aria-activedescendant`, and the listbox
// renders that option with `aria-selected="true"` + a visual
// highlight. Screen readers announce option changes without DOM
// focus shifting, which is what lets the textbox stay editable
// during navigation.
//
// Exposes:
//   - activeIndex   — currently-highlighted option (-1 when none)
//   - activeId      — the matching DOM id for aria-activedescendant
//   - onKeyDown     — handler the parent wires into the input
//   - setActiveIndex — for mouse-hover sync (popover's onMouseEnter)
//
// The hook is purely about navigation. Activation (Enter on an
// active option) and dismissal (Escape) are forwarded to callbacks
// the parent supplies — that keeps the popover's internal action
// catalog (geolocate vs. pick-city) out of this file.

import { useState, useEffect } from 'react';

export default function useComboboxNav({
  isOpen,
  optionCount,
  listboxId,
  onActivate,
  onClose,
}) {
  const [activeIndex, setActiveIndex] = useState(-1);

  // Reset on close so re-opening the popover doesn't restore the
  // last session's highlight on whichever option was active. Fresh
  // open should start with no active option — first ArrowDown moves
  // to index 0, matching how Airbnb / Google's combobox behaves.
  useEffect(() => {
    if (!isOpen) setActiveIndex(-1);
  }, [isOpen]);

  const onKeyDown = (e) => {
    if (!isOpen || optionCount <= 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % optionCount);
        return;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? optionCount - 1 : i - 1));
        return;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        return;
      case 'End':
        e.preventDefault();
        setActiveIndex(optionCount - 1);
        return;
      case 'Enter':
        if (activeIndex >= 0) {
          e.preventDefault();
          onActivate?.(activeIndex);
        }
        return;
      case 'Escape':
        // stopPropagation contains the dismiss to the combobox: a
        // parent modal, drawer, or other Escape-listening surface
        // would otherwise also fire its own Escape handler in the
        // same keypress and tear down the surrounding UI alongside
        // the listbox. WAI-ARIA combobox pattern contract is
        // single-step dismiss — Escape closes the listbox and
        // leaves everything else alone.
        e.preventDefault();
        e.stopPropagation();
        onClose?.();
        return;
      default:
    }
  };

  const activeId = activeIndex >= 0 && listboxId
    ? `${listboxId}-option-${activeIndex}`
    : undefined;

  return { activeIndex, activeId, onKeyDown, setActiveIndex };
}
